<template>
  <div class="wrapper">
    <section-heading
      v-if="title"
      :heading-color="headingColor"
      :heading-color-hex="headingColorHex"
      :heading-font-size="headingFontSize"
      :heading-align="headingAlign"
      :heading-type="headingType"
      :heading-background-image-url="headingBackgroundImageUrl"
      :heading-background-image-repeat="headingBackgroundImageRepeat"
      :title="title"
      :is-mobile-view="isMobileView"
      class="container"
    />
    <template v-if="showNewsItemClasses && visibleNewsItemClasses.length > 2">
      <div
        class="row items-center q-mt-md container relative-position"
        style="overflow: hidden;"
        :class="{
          'q-mb-xs justify-end': isMobileView,
          'q-mb-md justify-start': !isMobileView,
          'text-white': isExploreNewsItemTextLight
        }"
      >
        <q-tabs
          v-if="!isMobileView"
          v-model="selectedNewsItemClassId"
          dense
          class="col-auto q-pa-xs q-gutter-sm text-subtitle1"
          active-color="primary"
          indicator-color="primary"
          align="justify"
        >
          <q-tab
            v-for="newsItemClass in visibleNewsItemClasses"
            :key="newsItemClass.key"
            :name="newsItemClass.name"
            :label="newsItemClass.label"
            :style="newsItemClass.style"
          />
        </q-tabs>
        <q-select
          v-else
          v-model="selectedNewsItemClassId"
          dense
          :options="visibleNewsItemClasses"
          class="col-auto q-px-md"
          filled
          bg-color="transparent"
          :dark="isExploreNewsItemTextLight"
          map-options
          emit-value
        />
      </div>
    </template>
    <div
      class="row content-start justify-left"
      :style="{
        'margin-left': !isMobileView ? `-${padding}px` : undefined,
        'margin-right': !isMobileView ? `-${padding}px` : undefined
      }"
    >
      <div
        v-for="newsItem in visibleNewsItems"
        :key="newsItem.id"
        class="text-center"
        style="display: flex; flex-flow: row wrap;"
        :style="{
          'width': minWidth,
          padding: padding + 'px'
        }"
      >
        <q-card
          flat
          class="full-width"
          :class=" {
            'bg-transparent': backgroundColor === null,
            [`bg-${backgroundColor}`]: backgroundColor !== 'custom' && backgroundColor !== null,
            'cursor-pointer': newsItem.hasAction
          }"
          :style="{
            'background-color': backgroundColor === 'custom' ? backgroundColorHex : undefined,
            'border-radius': `${borderRadius}px`
          }"
          @click="handleClick(newsItem)"
        >
          <q-card-section
            v-if="(showExploreNewsItemBanner && !!newsItem.imageUrl) || (showExploreNewsItemSubtitle && !!newsItem.subtitle)"
            class="q-pa-none"
            style="min-height: 30px;"
          >
            <q-img
              v-if="showExploreNewsItemBanner && !!newsItem.imageUrl"
              :src="keepImageRatio ? newsItem.imageUrlOriginalRatio : newsItem.imageUrl"
              :style="{
                'border-top-left-radius': `${borderRadius}px`,
                'border-top-right-radius': `${borderRadius}px`,
                'border-bottom-left-radius': (showExploreNewsItemSubtitle && newsItem.subtitle) || (showExploreNewsItemDate && getRelativeNewsDate(newsItem))? '0' : `${borderRadius}px`,
                'border-bottom-right-radius': showExploreNewsItemSubtitle && newsItem.subtitle ? '0' : `${borderRadius}px`
              }"
              :alt="newsItem.title"
              :class="('shadow-' + shadow)"
            />

            <q-card-section
              v-if="showExploreNewsItemSubtitle && newsItem.subtitle"
              class="q-py-xs text-center text-uppercase text-bold inset-shadow"
              :class="!!newsItem.color && $isColorDark(newsItem.color) ? 'text-white' : 'text-dark'"
              :style="{
                'background-color': newsItem.color,
                'border-bottom-left-radius': (showExploreNewsItemBanner && !!newsItem.imageUrl && !(showExploreNewsItemDate && getRelativeNewsDate(newsItem))) ? `${borderRadius}px` : undefined ,
                'border-bottom-right-radius': (showExploreNewsItemBanner && !!newsItem.imageUrl) ? `${borderRadius}px` : undefined
              }"
            >
              {{ newsItem.subtitle }}
            </q-card-section>
            <span
              v-if="showExploreNewsItemClassNames && getNewItemsClassName(newsItem) && showExploreNewsItemBanner && !!newsItem.imageUrl"
              class="absolute-top-left bg-primary q-px-md q-py-xs text-subtitle2"
              :class="{
                'q-mt-lg': showExploreNewsItemBanner && !!newsItem.imageUrl,
                'text-white': $isColorDark(currentSite.primaryColor),
                'text-dark': !$isColorDark(currentSite.primaryColor)
              }"
              style="z-index: 2"
            >
              {{ getNewItemsClassName(newsItem) }}
            </span>
          </q-card-section>
          <q-card-section
            horizontal
          >
            <q-card-section
              v-if="showExploreNewsItemDate && getRelativeNewsDate(newsItem)"
              class="item-date col-auto full-height"
              :class="{
                'bg-primary': !newsItem.color,
                'text-white': !$_.isEmpty(newsItem.color) ? $isColorDark(newsItem.color) : $isColorDark(currentSite.primaryColor),
                'text-dark': newsItem.color ? !$isColorDark(newsItem.color) : !$isColorDark(currentSite.primaryColor)
              }"
              :style="{ 'background-color': newsItem.color }"
            >
              <div
                class="text-h6 text-center"
                :class="{
                  'column q-py-md': hasTitleOrDescriptionOrButtonOrClassNameShow(newsItem),
                  'text-white': $isColorDark(currentSite.primaryColor),
                  'text-dark': !$isColorDark(currentSite.primaryColor)
                }"
                style="line-height: 1.2rem;"
              >
                {{ formatDate(getRelativeNewsDate(newsItem), 'MMM') }}
                <span
                  :class="{ 'text-h3': hasTitleOrDescriptionOrButtonOrClassNameShow(newsItem) }"
                >
                  {{ formatDate(getRelativeNewsDate(newsItem), 'D') }}
                </span>
                {{ formatDate(getRelativeNewsDate(newsItem), 'YYYY') }}
              </div>
            </q-card-section>
            <q-card-section
              v-if="hasTitleOrDescriptionOrButtonOrClassNameShow(newsItem)"
              class="col"
              :class="{
                'text-left': !textCentred,
                'q-pt-none': !backgroundColor && showExploreNewsItemDate && getRelativeNewsDate(newsItem) && !(showExploreNewsItemSubtitle && !!newsItem.subtitle)
              }"
            >
              <span
                v-if="showExploreNewsItemClassNames && getNewItemsClassName(newsItem) && (!showExploreNewsItemBanner || !newsItem.imageUrl)"
                class="text-primary text-subtitle2"
              >
                {{ getNewItemsClassName(newsItem) }}
              </span>
              <div
                v-if="showExploreNewsItemTitle && !!newsItem.title"
                class="text-h5 q-pb-sm"
                :class="{
                  'text-white': isExploreNewsItemTextLight,
                  'text-left': !textCentred
                }"
              >
                {{ newsItem.title }}
              </div>
              <div
                v-if="showExploreNewsItemDescription && !!newsItem.description"
                class="ellipsis-3-lines"
                :class="{
                  'text-white': isExploreNewsItemTextLight,
                  'text-left': !textCentred
                }"
              >
                {{ newsItem.description }}
                <q-tooltip>
                  {{ newsItem.description }}
                </q-tooltip>
              </div>
              <div
                v-if="showExploreNewsItemButton && !!newsItem.buttonText"
                :class="{ 'text-left': !textCentred }"
                class="q-mt-sm"
              >
                <q-btn
                  flat
                  padding="sm none"
                  :ripple="false"
                  class="btn-link btn--no-hover"
                  :class="isExploreNewsItemTextLight ? 'text-white' : 'text-secondary'"
                >
                  {{ newsItem.buttonText }}
                </q-btn>
              </div>
            </q-card-section>
          </q-card-section>
          <q-menu
            v-if="currentUser && currentUser.admin"
            touch-position
            context-menu
          >
            <q-list style="width: 200px;">
              <q-item
                v-close-popup
                clickable
                @click="editNewsItems = true"
              >
                <q-item-section avatar>
                  <q-icon name="edit" />
                </q-item-section>
                <q-item-section>
                  Edit News Items
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>
          <q-dialog
            v-model="editNewsItems"
            full-width
          >
            <news-item-table />
          </q-dialog>
        </q-card>
      </div>
    </div>
  </div>
</template>

<script>
import { date } from 'quasar'
import { DISPLAY_DATE_TYPES } from 'src/models/page/Section'
import { defineAsyncComponent } from 'vue'

const { formatDate } = date

export default {
  name: 'ExploreNews',
  configuration: ['newsItemClasses', 'currentSite'],
  context: ['currentUser'],
  components: {
    SectionHeading: defineAsyncComponent(() => import('src/modules/consumer/components/SectionHeading')),
    NewsItemTable: defineAsyncComponent(() => import('src/modules/admin/news-item/NewsItemTable'))
  },
  props: {
    headingColor: {
      type: String,
      default: 'primary',
      required: false
    },
    headingColorHex: {
      type: String,
      default: '#FFFFFF',
      required: false
    },
    headingAlign: {
      type: String,
      default: null,
      required: false
    },
    headingFontSize: {
      type: String,
      default: null,
      required: false
    },
    headingType: {
      type: String,
      default: 'default',
      required: false
    },
    headingBackgroundImageUrl: {
      type: String,
      default: null,
      required: false
    },
    headingBackgroundImageRepeat: {
      type: Boolean,
      required: false,
      default: true
    },
    title: {
      type: String,
      required: false,
      default: null
    },
    newsItems: {
      type: Array,
      required: true
    },
    newsItemsClassIds: {
      type: Array,
      required: false,
      default: () => ([])
    },
    initialNewsItemClassId: {
      type: String,
      required: false,
      default: null
    },
    showNewsItemClasses: {
      type: Boolean,
      required: false,
      default: true
    },
    showExploreNewsItemBanner: {
      type: Boolean,
      required: false,
      default: true
    },
    showExploreNewsItemTitle: {
      type: Boolean,
      required: false,
      default: true
    },
    showExploreNewsItemSubtitle: {
      type: Boolean,
      required: false,
      default: false
    },
    showExploreNewsItemDescription: {
      type: Boolean,
      required: false,
      default: true
    },
    showExploreNewsItemButton: {
      type: Boolean,
      required: false,
      default: false
    },
    showExploreNewsItemDate: {
      type: Boolean,
      required: false,
      default: false
    },
    showExploreNewsItemClassNames: {
      type: Boolean,
      required: false,
      default: false
    },
    newsDateType: {
      type: String,
      required: false,
      default: DISPLAY_DATE_TYPES.PUBLISHED
    },
    newsPerRow: {
      type: Number,
      required: false,
      default: 3
    },
    borderRadius: {
      type: Number,
      required: false,
      default: 3
    },
    padding: {
      type: Number,
      required: false,
      default: 15
    },
    shadow: {
      type: Number,
      required: false,
      default: 0
    },
    backgroundColor: {
      type: String,
      required: false,
      default: null
    },
    backgroundColorHex: {
      type: String,
      required: false,
      default: '#ffffff'
    },
    numberOfNewsOnShow: {
      type: Number,
      required: false,
      default: null
    },
    isExploreNewsItemTextLight: {
      type: Boolean,
      required: false,
      default: false
    },
    textCentred: {
      type: Boolean,
      required: false,
      default: true
    },
    keepImageRatio: {
      type: Boolean,
      required: false,
      default: false
    },
    isPhonePreview: {
      type: Boolean,
      required: false,
      default: false
    },
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data () {
    return {
      selectedNewsItemClassId: this.initialNewsItemClassId,
      newsItemClassTabs: null,
      editNewsItems: false
    }
  },
  computed: {
    visibleNewsItems () {
      const visibleNewsItems = _.filter(this.newsItems, newsItem => {
        return (newsItem.visible &&
        (!this.selectedNewsItemClassId || this.selectedNewsItemClassId === newsItem.newsItemClassId) &&
        (!this.newsItemsClassIds.length || _.includes(this.newsItemsClassIds, newsItem.newsItemClassId)))
      })

      if (this.numberOfNewsOnShow !== null && this.numberOfNewsOnShow !== '') return visibleNewsItems.slice(0, this.numberOfNewsOnShow)
      return visibleNewsItems
    },
    visibleNewsItemClasses () {
      const visibleNewsItemClasses = [{
        key: null,
        name: null,
        value: null,
        label: this.$i18n.t('collection.all'),
        style: `{'border-radius' : ${this.newsItemBorderRadius}px}`
      }]

      _.each(this.newsItemClasses.data, newsItemClass => {
        if (_.includes(this.newsItemsClassIds, newsItemClass.id)) {
          visibleNewsItemClasses.push({
            key: newsItemClass.id,
            name: newsItemClass.id,
            value: newsItemClass.id,
            label: newsItemClass.name,
            style: `{'border-radius' : ${this.newsItemBorderRadius}px}`
          })
        }
      })

      return visibleNewsItemClasses
    },
    minWidth () {
      return (this.isMobileView && '100%') ||
          (this.$q.screen.sm && '50%') ||
          (this.$q.screen.md && '50%') ||
          100 / this.newsPerRow + '%'
    }

  },
  created () {
    _.extend(this, {
      formatDate,
      DISPLAY_DATE_TYPES
    })
  },
  methods: {
    handleClick (newsItem) {
      if (!newsItem || !newsItem.hasAction) return

      this.$trackEvent(this.$customerEvents.CLICKED_NEWS_ITEM, { id: newsItem.id, title: newsItem.title })

      newsItem.route(this.$router)
    },
    getRelativeNewsDate (newsItem) {
      if (this.newsDateType === DISPLAY_DATE_TYPES.PUBLISHED && newsItem.publishedAt) return newsItem.publishedAt.i18n('date')
      if (this.newsDateType === DISPLAY_DATE_TYPES.START && newsItem.startDate) return newsItem.startDate.i18n('date')
      if (this.newsDateType === DISPLAY_DATE_TYPES.END && newsItem.endDate) return newsItem.endDate.i18n('date')
    },
    getNewItemsClassName (newsItem) {
      const getNewItemsClassName = {}

      _.each(this.newsItemClasses.data, newsItemClass => {
        getNewItemsClassName[newsItemClass.id] = newsItemClass.name
      })

      return getNewItemsClassName[newsItem.newsItemClassId]
    },
    hasTitleOrDescriptionOrButtonOrClassNameShow (newsItem) {
      return (this.showExploreNewsItemTitle && !!newsItem.title) ||
      (this.showExploreNewsItemDescription && !!newsItem.description) ||
      (this.showExploreNewsItemButton && !!newsItem.buttonText) ||
      (this.showExploreNewsItemClassNames && this.getNewItemsClassName(newsItem) && (!this.showExploreNewsItemBanner || !newsItem.imageUrl))
    }

  },
  trackPage: true
}
</script>
